import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Chip,
    Alert,
    AlertTitle,
} from "@mui/material";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { getBillingPreview } from "../redux/ducks/invoices";

const HelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const ControlRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
}));

const TotalsBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.surface?.inset || theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
}));

const TotalItem = styled(Box)({
    minWidth: 110,
});

const TotalValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const EntityBox = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(3),
}));

const EntityHeader = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
}));

const EntityName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const CenteredBox = styled(Box)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
}));

const money = (n) => `$ ${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const BREAKDOWN_LABELS = {
    retainerBase: "Retainer base",
    overage: "Overage",
    hourly: "Hourly work",
    charges: "Charges",
    payments: "Fixed price",
};

const currentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const lineKindLabel = (line) => {
    if (line.isRetainerBase) return "Retainer";
    if (line.isRetainerOverage) return "Overage";
    if (line.isPayment) return "Payment";
    return line.billingType || null;
};

const BillingPreviewDialog = ({ open, client, onClose }) => {
    const dispatch = useDispatch();
    const [month, setMonth] = useState(currentMonth);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback((forMonth) => {
        if (!client?._id) return;
        setLoading(true);
        dispatch(getBillingPreview(client._id, forMonth, (data) => {
            setLoading(false);
            setPreview(data || null);
        }));
    }, [client, dispatch]);

    useEffect(() => {
        if (!open) return;
        const initial = currentMonth();
        setMonth(initial);
        setPreview(null);
        load(initial);
    }, [open, load]);

    const handleMonthChange = (e) => {
        const next = e.target.value;
        setMonth(next);
        if (/^\d{4}-\d{2}$/.test(next)) load(next);
    };

    const entities = preview?.entities || [];
    const orphans = preview?.orphans || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Billing preview — {client?.name}</DialogTitle>
            <DialogContent dividers>
                <HelperText variant="body2">
                    What this client would be invoiced if billing ran for the month below. Nothing is created or changed by looking.
                </HelperText>

                <ControlRow>
                    <TextField
                        label="Month"
                        type="month"
                        size="small"
                        value={month}
                        onChange={handleMonthChange}
                        InputLabelProps={{ shrink: true }}
                    />
                    {preview && !loading && (
                        <Typography variant="body2" color="text.secondary">
                            {entities.length === 0
                                ? "Nothing would be invoiced for this month."
                                : `${entities.length} invoice${entities.length === 1 ? "" : "s"} would be generated.`}
                        </Typography>
                    )}
                </ControlRow>

                {loading ? (
                    <CenteredBox>
                        <CircularProgress />
                    </CenteredBox>
                ) : !preview ? (
                    <Typography variant="body2" color="text.secondary">
                        The preview could not be loaded.
                    </Typography>
                ) : (
                    <>
                        <TotalsBox>
                            <TotalItem>
                                <TotalValue variant="h6">{money(preview.total)}</TotalValue>
                                <Typography variant="caption" color="text.secondary">Total</Typography>
                            </TotalItem>
                            {Object.entries(BREAKDOWN_LABELS).map(([key, label]) => (
                                <TotalItem key={key}>
                                    <TotalValue variant="body1">{money(preview.breakdown?.[key])}</TotalValue>
                                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                                </TotalItem>
                            ))}
                        </TotalsBox>

                        {orphans.length > 0 && (
                            <Alert severity="warning" sx={{ marginBottom: 2 }}>
                                <AlertTitle>Some work would not reach an invoice</AlertTitle>
                                {orphans.map((orphan) => (
                                    <Typography key={orphan.reason} variant="body2">
                                        {orphan.count} × {orphan.reason}
                                    </Typography>
                                ))}
                            </Alert>
                        )}

                        {entities.map((entity) => (
                            <EntityBox key={`${entity.entityType}-${entity.entityId}`}>
                                <EntityHeader>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Chip size="small" variant="outlined" label={entity.entityType} />
                                        <EntityName variant="subtitle2">{entity.name}</EntityName>
                                    </Box>
                                    <EntityName variant="subtitle2">{money(entity.total)}</EntityName>
                                </EntityHeader>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Description</TableCell>
                                                <TableCell align="right">Hours</TableCell>
                                                <TableCell align="right">Rate</TableCell>
                                                <TableCell align="right">Amount</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {entity.lineItems.map((line, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {line.description}
                                                        {lineKindLabel(line) && (
                                                            <Chip size="small" variant="outlined" label={lineKindLabel(line)} sx={{ marginLeft: 1 }} />
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">{line.hours ? line.hours.toFixed(2) : ""}</TableCell>
                                                    <TableCell align="right">{line.rate ? money(line.rate) : ""}</TableCell>
                                                    <TableCell align="right">{money(line.amount)}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </EntityBox>
                        ))}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default BillingPreviewDialog;
